/**
 * ASCII Art Generator - 自适应性能监控
 *
 * FPS监控与动态降级策略
 * Wave 4 完整实现
 */

// ============================================
// 类型定义
// ============================================

/** 性能监控配置 */
export interface PerformanceMonitorOptions {
  /** 目标FPS，默认 60 */
  targetFPS?: number
  /** 样本大小（用于计算平均FPS），默认 30 */
  sampleSize?: number
  /** 降级阈值（低于目标FPS的比例），默认 0.7 */
  degradeThreshold?: number
  /** 降级回调 */
  onDegrade?: (level: DegradeLevel) => void
  /** 恢复回调 */
  onRecover?: () => void
}

/** 降级级别 */
export type DegradeLevel = 'none' | 'light' | 'medium' | 'heavy'

/** 性能监控状态 */
export interface PerformanceMonitorState {
  /** 当前FPS */
  currentFPS: number
  /** 平均FPS */
  averageFPS: number
  /** 最小FPS */
  minFPS: number
  /** 最大FPS */
  maxFPS: number
  /** 当前降级级别 */
  degradeLevel: DegradeLevel
  /** 是否正在降级 */
  isDegrading: boolean
  /** 总帧数 */
  totalFrames: number
}

// ============================================
// 性能监控类
// ============================================

/**
 * 性能监控器
 *
 * 功能：
 * - FPS监控（记录最近N帧）
 * - 动态调整渲染质量（降级策略）
 * - 自动恢复机制
 */
export class PerformanceMonitor {
  private options: PerformanceMonitorOptions
  private state: PerformanceMonitorState
  private frameTimes: number[] = []
  private degradeCounter: number = 0
  private recoverCounter: number = 0
  private lastDegradeTime: number = 0

  // 降级配置
  private degradeLevels: Record<DegradeLevel, { fpsRatio: number; cooldownMs: number }> = {
    'none': { fpsRatio: 1, cooldownMs: 0 },
    'light': { fpsRatio: 0.85, cooldownMs: 5000 },
    'medium': { fpsRatio: 0.7, cooldownMs: 10000 },
    'heavy': { fpsRatio: 0.5, cooldownMs: 15000 }
  }

  constructor(options: PerformanceMonitorOptions = {}) {
    this.options = {
      targetFPS: options.targetFPS ?? 60,
      sampleSize: options.sampleSize ?? 30,
      degradeThreshold: options.degradeThreshold ?? 0.7,
      onDegrade: options.onDegrade,
      onRecover: options.onRecover
    }

    this.state = {
      currentFPS: this.options.targetFPS!,
      averageFPS: this.options.targetFPS!,
      minFPS: this.options.targetFPS!,
      maxFPS: this.options.targetFPS!,
      degradeLevel: 'none',
      isDegrading: false,
      totalFrames: 0
    }
  }

  // ============================================
  // FPS监控
  // ============================================

  /**
   * 记录一帧的时间
   *
   * @param frameTime 帧时间（ms）
   */
  recordFrame(frameTime: number): void {
    this.state.totalFrames++

    // 计算当前FPS
    const fps = Math.round(1000 / frameTime)
    this.state.currentFPS = fps

    // 添加到样本数组
    this.frameTimes.push(frameTime)
    if (this.frameTimes.length > this.options.sampleSize!) {
      this.frameTimes.shift()
    }

    // 计算统计数据
    this.calculateStats()

    // 检查是否需要降级或恢复
    this.checkPerformance()
  }

  /**
   * 计算FPS统计数据
   */
  private calculateStats(): void {
    if (this.frameTimes.length < 3) return

    // 计算平均帧时间
    const avgFrameTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length
    this.state.averageFPS = Math.round(1000 / avgFrameTime)

    // 计算最小和最大FPS
    const frameRates = this.frameTimes.map(t => 1000 / t)
    this.state.minFPS = Math.round(Math.min(...frameRates))
    this.state.maxFPS = Math.round(Math.max(...frameRates))
  }

  // ============================================
  // 降级策略
  // ============================================

  /**
   * 检查性能状态，决定是否降级或恢复
   */
  private checkPerformance(): void {
    const fpsRatio = this.state.averageFPS / this.options.targetFPS!

    // 如果FPS低于阈值，增加降级计数
    if (fpsRatio < this.options.degradeThreshold!) {
      this.degradeCounter++
      this.recoverCounter = 0

      // 连续多帧低于阈值才触发降级
      if (this.degradeCounter >= 10) {
        this.handleDegrade()
      }
    } else {
      // FPS恢复正常
      this.degradeCounter = 0
      this.recoverCounter++

      // 连续多帧高于阈值才触发恢复
      if (this.recoverCounter >= 20 && this.state.degradeLevel !== 'none') {
        this.handleRecover()
      }
    }
  }

  /**
   * 处理性能降级
   */
  private handleDegrade(): void {
    const now = performance.now()
    const cooldown = this.degradeLevels[this.state.degradeLevel].cooldownMs

    // 检查冷却时间
    if (now - this.lastDegradeTime < cooldown) {
      return
    }

    // 根据当前级别决定下一个级别
    let nextLevel: DegradeLevel = 'none'

    if (this.state.degradeLevel === 'none') {
      nextLevel = 'light'
    } else if (this.state.degradeLevel === 'light') {
      nextLevel = 'medium'
    } else if (this.state.degradeLevel === 'medium') {
      nextLevel = 'heavy'
    } else {
      // 已经是最重降级，不再降级
      return
    }

    // 更新状态
    this.state.degradeLevel = nextLevel
    this.state.isDegrading = true
    this.lastDegradeTime = now
    this.degradeCounter = 0

    // 调用降级回调
    if (this.options.onDegrade) {
      this.options.onDegrade(nextLevel)
    }

    console.warn(`Performance degrade to level: ${nextLevel}`)
  }

  /**
   * 处理性能恢复
   */
  private handleRecover(): void {
    // 恢复到上一级别
    let nextLevel: DegradeLevel = 'none'

    if (this.state.degradeLevel === 'heavy') {
      nextLevel = 'medium'
    } else if (this.state.degradeLevel === 'medium') {
      nextLevel = 'light'
    } else if (this.state.degradeLevel === 'light') {
      nextLevel = 'none'
    }

    // 更新状态
    this.state.degradeLevel = nextLevel
    this.state.isDegrading = nextLevel !== 'none'
    this.recoverCounter = 0

    // 调用恢复回调
    if (this.options.onRecover) {
      this.options.onRecover()
    }

    if (nextLevel === 'none') {
      console.info('Performance recovered to normal')
    } else {
      console.info(`Performance recovered to level: ${nextLevel}`)
    }
  }

  // ============================================
  // 公共方法
  // ============================================

  /**
   * 检查是否应该降级
   */
  shouldDegrade(): boolean {
    return this.state.isDegrading && this.state.degradeLevel !== 'none'
  }

  /**
   * 获取当前FPS
   */
  getCurrentFPS(): number {
    return this.state.currentFPS
  }

  /**
   * 获取平均FPS
   */
  getAverageFPS(): number {
    return this.state.averageFPS
  }

  /**
   * 获取当前降级级别
   */
  getDegradeLevel(): DegradeLevel {
    return this.state.degradeLevel
  }

  /**
   * 获取完整状态
   */
  getState(): PerformanceMonitorState {
    return { ...this.state }
  }

  /**
   * 获取建议的目标FPS（基于当前降级级别）
   */
  getSuggestedTargetFPS(): number {
    const ratio = this.degradeLevels[this.state.degradeLevel].fpsRatio
    return Math.round(this.options.targetFPS! * ratio)
  }

  /**
   * 手动设置降级级别
   */
  setDegradeLevel(level: DegradeLevel): void {
    this.state.degradeLevel = level
    this.state.isDegrading = level !== 'none'
    this.lastDegradeTime = performance.now()
  }

  /**
   * 重置监控器
   */
  reset(): void {
    this.frameTimes = []
    this.degradeCounter = 0
    this.recoverCounter = 0
    this.lastDegradeTime = 0
    this.state = {
      currentFPS: this.options.targetFPS!,
      averageFPS: this.options.targetFPS!,
      minFPS: this.options.targetFPS!,
      maxFPS: this.options.targetFPS!,
      degradeLevel: 'none',
      isDegrading: false,
      totalFrames: 0
    }
  }

  /**
   * 更新配置
   */
  updateOptions(options: Partial<PerformanceMonitorOptions>): void {
    this.options = { ...this.options, ...options }
  }

  /**
   * 销毁监控器
   */
  destroy(): void {
    this.frameTimes = []
    this.options.onDegrade = undefined
    this.options.onRecover = undefined
  }
}

// ============================================
// 工厂函数
// ============================================

/**
 * 创建性能监控器
 */
export function createPerformanceMonitor(
  options?: PerformanceMonitorOptions
): PerformanceMonitor {
  return new PerformanceMonitor(options)
}